import { defineModule } from 'direct-vuex'
import { moduleActionContext } from '..'
import * as db from '@shared/types/database'
import router from '@/router'
import api from '@/api'
import { setAuthToken, removeAuthToken } from '@/utilities/auth-token'
import { Session, GroupsPermission } from '@shared/types/graphql'
import { identifyForMonitoring } from '@/analytics/monitoring'
import { getUserInfo } from '@/analytics/ClientAnalyticsProvider'

export interface UserState {
  authenticatedUser?: Session
  acl: GroupsPermission | null
}

const createInitialState = (): UserState => ({
  authenticatedUser: undefined,
  acl: null
})

const user = defineModule({
  namespaced: true,
  state: createInitialState,
  getters: {
    username (state: UserState) {
      const { authenticatedUser } = state
      if (!authenticatedUser) {
        return ''
      }

      const { firstName, lastName } = authenticatedUser
      return `${firstName} ${lastName}`
    },
    hasFeature: (state: UserState) => (feature: string) => {
      const { authenticatedUser } = state

      if (!authenticatedUser) {
        return false
      }

      const { featureConfig } = authenticatedUser
      const features = Object.keys(featureConfig).filter((featureKey) => featureConfig[featureKey].enabled)
      return features.includes(feature)
    },
    allWorkspaces (state: UserState) {
      return state.authenticatedUser ? state.authenticatedUser.workspaces : []
    },
    authenticatedUser (state: UserState): Session {
      if (!state.authenticatedUser) {
        throw new Error('No authenticated user found')
      }

      return state.authenticatedUser
    }
  },
  mutations: {
    changeFirstName (state: UserState, firstName: string) {
      if (state.authenticatedUser) {
        state.authenticatedUser.firstName = firstName
      }
    },
    setSession (state: UserState, session: Session) {
      state.authenticatedUser = session
    },
    reset (state: UserState) {
      Object.assign(state, createInitialState())
    },
    updateAcl (state: UserState, userAcl: GroupsPermission) {
      state.acl = userAcl
    }
  },
  actions: {
    async login (context, payload: db.LoginRequest) {
      const res = await api.login(payload)
      if (res.redirect) {
        window.location.replace(res.redirect)
      }

      setAuthToken(res.token)
      // TODO Change the API's login endpoint to send us the complete session data.
      // Since we don't get project/test count from the login API, we have to
      // use the GraphQL one instead.
      const session = await api.getSession()
      if (!session) {
        throw new Error('Expected an authenticated session')
      }

      const { commit } = userActionContext(context)
      commit.setSession(session)

      const userInfo = getUserInfo()
      if (userInfo) {
        identifyForMonitoring(userInfo)
      }
      return res
    },
    logout (context) {
      api.logout()
      removeAuthToken()

      const { rootDispatch, dispatch } = userActionContext(context)
      rootDispatch.reset()
      dispatch.reset()
      router.push('/login')
    },
    async fetchAcl (context, groupId: string) {
      const { commit } = userActionContext(context)
      const userPermission = await api.getUserAcl(groupId)
      commit.updateAcl(userPermission)
    },
    setSession (context, session: Session) {
      const { commit } = userActionContext(context)
      commit.setSession(session)
    },
    reset (context) {
      const { commit } = userActionContext(context)
      commit.reset()
    }
  }
})

export default user
export const userActionContext = (context: any) => moduleActionContext(context, user)
